import React, { useState, useMemo } from "react";
import type {
  StepMap,
  StepProps,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import {
  FooterButton,
  FooterContainer,
  StepTitle,
  StepContent,
  CheckableOption,
  OptionsSection,
  OtherInput,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import { observer } from "mobx-react-lite";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { posthogClientManager } from "@/manager/posthog-client";
import { MDSIcon, MDSIconColor, MDSIconKind, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";

interface IcpRfiOption {
  id: string;
  title: string;
}

const ProfileOptions: IcpRfiOption[] = [
  { id: "business-leadership", title: "I'm in a business leadership role" },
  { id: "adhd", title: "I have ADHD" },
  { id: "researcher-professor", title: "I'm a researcher or professor" },
  { id: "student", title: "I'm a student" },
  { id: "multi-company", title: "I do work across multiple companies" },
  { id: "retired", title: "I'm retired" },
  { id: "writer", title: "I'm a writer" },
  { id: "other", title: "Other" },
];

const randomizeProfileOptions = (options: IcpRfiOption[]): IcpRfiOption[] => {
  const otherOption = options.find(option => option.id === "other");
  const nonOtherOptions = options.filter(option => option.id !== "other");

  const shuffled = [...nonOtherOptions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  // Add "other" option at the end if it exists
  return otherOption ? [...shuffled, otherOption] : shuffled;
};

export const IcpRfiStep: React.FC<StepProps> = observer(({ onNavToStep }) => {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState("");

  const randomizedOptions = useMemo(() => randomizeProfileOptions(ProfileOptions), []);

  const handleOptionToggle = (optionId: string, e: React.MouseEvent) => {
    const newSelected = new Set(selectedOptions);
    const wasSelected = newSelected.has(optionId);

    if (wasSelected && optionId === "other" && !(e.target instanceof SVGElement)) {
      // to avoid the space bar un-toggline the Other option, you have to click on
      // the icon to un-toggle Other
      return;
    }

    if (wasSelected) {
      newSelected.delete(optionId);
      if (optionId === "other") {
        setOtherText("");
      }
    } else {
      newSelected.add(optionId);
    }
    setSelectedOptions(newSelected);
  };

  const hasOtherWithText = selectedOptions.has("other") && otherText.trim().length > 0;
  const hasNonOtherSelections = Array.from(selectedOptions).some(id => id !== "other");

  const handleNext = async () => {
    // Track step completion with user selections
    const selectedOptionIds = Array.from(selectedOptions).filter(
      id => id !== "other" || hasOtherWithText
    );
    const selectedOptionTitles = selectedOptionIds
      .map(id => ProfileOptions.find(opt => opt.id === id)?.title)
      .filter(Boolean);

    const properties = {
      selectedOptions: selectedOptionIds,
      selectedOptionTitles,
      otherText: hasOtherWithText ? otherText.trim() : undefined,
      totalOptionsSelected: selectedOptionIds.length,
    };
    trackEvent(TrackedEvent.IcpRfiStepComplete, properties);

    // Set ICP RFI data as PostHog person properties
    const posthogUserProperties = {
      icp_profile_options: selectedOptionIds,
      icp_profile_labels: selectedOptionTitles,
      icp_profile_other_text: hasOtherWithText ? otherText.trim() : undefined,
      icp_profile_count: selectedOptionIds.length,
      icp_profile_completed: true,
      icp_profile_completed_at: new Date().toISOString(),
    };

    const posthogIcpOnceProperties = {
      initial_icp_profile_completed_at: new Date().toISOString(),
    };

    posthogClientManager.setPersonProperties(posthogUserProperties, posthogIcpOnceProperties);

    // Navigate to referrer step
    onNavToStep?.("referrer");
  };

  const isNextEnabled = hasNonOtherSelections || hasOtherWithText;

  return (
    <WizardContentWrapper>
      <StepTitle>Welcome to Mem</StepTitle>
      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Select the options that best describe you. Select at least one.
        </MDSText>
      </StepContent>

      <StepContent overflowY="auto">
        <OptionsSection>
          {randomizedOptions.map(option => (
            <CheckableOption
              key={option.id}
              isSelected={selectedOptions.has(option.id)}
              onClick={e => handleOptionToggle(option.id, e)}
              isOtherOption={option.id === "other"}
              hasOtherText={option.id === "other" ? hasOtherWithText : false}
            >
              {option.id === "other" && selectedOptions.has("other") ? (
                <OtherInput
                  placeholder="Describe your role"
                  value={otherText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOtherText(e.target.value)
                  }
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                  onKeyUp={(e: React.KeyboardEvent) => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                  {option.title}
                </MDSText>
              )}
              {selectedOptions.has(option.id) &&
                (option.id !== "other" || otherText.trim().length > 0) && (
                  <MDSIcon
                    kind={MDSIconKind.CheckCircleSolid}
                    size={16}
                    color={MDSIconColor.Emphasis}
                  />
                )}
            </CheckableOption>
          ))}
        </OptionsSection>
      </StepContent>

      <FooterContainer>
        <FooterButton onClick={handleNext} isDisabled={!isNextEnabled}>
          Next
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

export const IcpRfiStepsMap: StepMap = {
  "user-profile": {
    id: "user-profile",
    group: "icp-rfi",
    render: props => <IcpRfiStep {...props} />,
    fullHeight: true,
  },
} as const;
